import { useState, useRef, useEffect } from 'react';

/**
 * MobileDropdown - Custom dropdown component built from scratch.
 * Does NOT use native <select> - uses only div/button/click handlers.
 * Works on ALL devices including iOS Safari.
 * 
 * Props:
 *  - options: Array of { value: string, label: string }
 *  - value: string (current selected value)
 *  - onChange: (value: string) => void
 *  - placeholder: string (default: "Seleccionar...")
 *  - testId: string (data-testid)
 *  - required: boolean
 */
export default function MobileDropdown({ options = [], value, onChange, placeholder = 'Seleccionar...', testId = '', required = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  // Close when tapping outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [open]);

  // Focus search when opened
  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  const selected = options.find(o => o.value === value);
  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }} data-testid={testId}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={testId ? `${testId}-trigger` : undefined}
        style={{
          width: '100%',
          minHeight: 48,
          padding: '12px 16px',
          backgroundColor: '#111',
          color: selected ? '#fff' : 'rgba(255,255,255,0.4)',
          border: open ? '2px solid #D4AF37' : '1px solid rgba(255,255,255,0.2)',
          borderRadius: 6,
          fontSize: 16,
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.3)', fontSize: 12, flexShrink: 0 }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {/* Hidden input for form required validation */}
      {required && (
        <input
          type="text"
          value={value || ''}
          required
          onChange={() => {}}
          style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
          tabIndex={-1}
        />
      )}

      {/* Dropdown Options List */}
      {open && (
        <div
          data-testid={testId ? `${testId}-list` : undefined}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 9999,
            marginTop: 4,
            backgroundColor: '#111',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 6,
            maxHeight: 280,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          }}
        >
          {/* Search field - always visible */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, backgroundColor: '#111', zIndex: 1 }}>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Escribe para buscar..."
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: '#0A0A0A',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 4,
                fontSize: 16,
                outline: 'none',
              }}
            />
          </div>

          {/* Options */}
          {filtered.length === 0 && (
            <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
              Sin resultados
            </div>
          )}
          {filtered.map((opt) => (
            <div
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              onTouchEnd={(e) => { e.preventDefault(); handleSelect(opt.value); }}
              data-testid={testId ? `${testId}-opt-${opt.value}` : undefined}
              style={{
                padding: '14px 16px',
                fontSize: 15,
                color: opt.value === value ? '#D4AF37' : '#fff',
                backgroundColor: opt.value === value ? 'rgba(212,175,55,0.1)' : 'transparent',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'rgba(212,175,55,0.2)',
                touchAction: 'manipulation',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
